/**
 * A plan's Activity feed: its Staffing Concerns (RP-34) and its comments — on the whole plan
 * or on a Plan Line — merged into one timeline, newest first.
 *
 * Pure: the two lists come from their own endpoints and are merged on the client, so each
 * keeps its own permissions and its own shape.
 */

import type { PlanConcernView } from "@/schemas/plan-concerns";
import type { PlanCommentView } from "@/schemas/resource-plans";

export type PlanActivityItem =
  | { kind: "concern"; at: string; concern: PlanConcernView }
  | { kind: "comment"; at: string; comment: PlanCommentView };

function itemId(item: PlanActivityItem): string {
  return item.kind === "concern" ? item.concern.id : item.comment.id;
}

/** Newest first; entries at the same instant keep a stable order by id. */
export function mergePlanActivity(
  concerns: readonly PlanConcernView[],
  comments: readonly PlanCommentView[],
): PlanActivityItem[] {
  const items: PlanActivityItem[] = [
    ...concerns.map((concern) => ({ kind: "concern" as const, at: concern.raisedAt, concern })),
    ...comments.map((comment) => ({ kind: "comment" as const, at: comment.createdAt, comment })),
  ];
  return items.sort((a, b) => {
    const byTime = Date.parse(b.at) - Date.parse(a.at);
    if (byTime !== 0) return byTime;
    const ia = itemId(a);
    const ib = itemId(b);
    return ia < ib ? -1 : ia > ib ? 1 : 0;
  });
}
